import { useEffect, useState } from 'react';
import { create } from 'zustand';

import { useSaveStore } from '../save/saveStore';

export type MotionPreference = 'system' | 'full' | 'reduced';

export interface StoredPreferences {
  readonly audioEnabled: boolean;
  readonly hapticsEnabled: boolean;
  readonly motionPreference: MotionPreference;
  readonly tutorialComplete: boolean;
}

interface UiPreferencesStore extends StoredPreferences {
  hydrate: (preferences: StoredPreferences) => void;
  setAudioEnabled: (enabled: boolean) => void;
  setHapticsEnabled: (enabled: boolean) => void;
  setMotionPreference: (preference: MotionPreference) => void;
  setTutorialComplete: (complete: boolean) => void;
}

export const TUTORIAL_STORAGE_KEY = 'rogue-brick-breaker:onboarding:m5';

const DEFAULT_PREFERENCES: StoredPreferences = {
  audioEnabled: true,
  hapticsEnabled: true,
  motionPreference: 'system',
  tutorialComplete: false,
};

export const useUiPreferencesStore = create<UiPreferencesStore>((set, get) => {
  const update = (patch: Partial<StoredPreferences>) => {
    const next = { ...selectPreferences(get()), ...patch };
    set(next);
    useSaveStore.getState().updatePreferences(patch);
  };
  return {
    ...DEFAULT_PREFERENCES,
    hydrate: (preferences) => set(preferences),
    setAudioEnabled: (audioEnabled) => update({ audioEnabled }),
    setHapticsEnabled: (hapticsEnabled) => update({ hapticsEnabled }),
    setMotionPreference: (motionPreference) => update({ motionPreference }),
    setTutorialComplete: (tutorialComplete) => update({ tutorialComplete }),
  };
});

export function useReducedMotionPreference(): boolean {
  const motionPreference = useUiPreferencesStore((state) => state.motionPreference);
  const [systemPrefersReducedMotion, setSystemPrefersReducedMotion] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = (event: MediaQueryListEvent) =>
      setSystemPrefersReducedMotion(event.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return motionPreference === 'reduced' ||
    (motionPreference === 'system' && systemPrefersReducedMotion)
    ? true
    : false;
}

function selectPreferences(state: UiPreferencesStore): StoredPreferences {
  return {
    audioEnabled: state.audioEnabled,
    hapticsEnabled: state.hapticsEnabled,
    motionPreference: state.motionPreference,
    tutorialComplete: state.tutorialComplete,
  };
}
